import { cva, type VariantProps } from 'class-variance-authority'

/**
 * The one look every floating list shares: Menu, ContextMenu, Select and Combobox. Each wraps a
 * different Base UI primitive, but a row in any of them is the same row, so the classes live here
 * and the components only add what their primitive needs.
 *
 * Popover shares `popupSurface` and nothing else, because its content is not a list.
 */

/**
 * The overlay ground at the panel radius. Its shadow is its edge, so it takes no border, and it
 * lands at once and fades out: opening answers a click, so any delay reads as lag.
 */
const popupGround = 'bg-background-overlay text-foreground corner-squircle rounded-[var(--radius-lg)] outline-none'
const popupFade =
  'transition-opacity duration-150 ease-[cubic-bezier(0.22,1,0.36,1)] data-[ending-style]:opacity-0 motion-reduce:transition-none'

export const popupSurface = [popupGround, 'shadow-lg', popupFade].join(' ')

/**
 * A column of rows 1px apart inside 4px of padding. Wide enough for a label and a shortcut, capped
 * so a long title truncates instead of stretching the list across the window, and scrolling once
 * it runs out of room.
 */
export const listPopup = [
  popupGround,
  // A list floats over content of every colour, so a hairline ring in the border at 60% outlines
  // it without framing it.
  '[box-shadow:var(--shadow-lg),0_0_0_var(--border-hairline)_color-mix(in_oklch,var(--border)_60%,transparent)]',
  // Lists land at once and only fade out: opening answers a click.
  popupFade,
  'flex flex-col gap-px p-1 min-w-56 max-w-[min(21rem,var(--available-width))] max-h-[var(--available-height)]',
  'overflow-y-auto overscroll-contain',
  // A description wraps rather than truncates, so a list that has one gets room to say it.
  'has-[[data-slot=list-item-description]]:max-w-[min(28rem,var(--available-width))]',
  // One first column for the whole list, Prism's 20px lane: once any row leads with an icon or a
  // lead, every row without one indents by the lane and its gap, so all labels start at 30px.
  '[&:has([data-slot=list-item]>:is(svg,img,[data-slot=list-item-lead]):not([data-slot=list-item-check],[data-slot=list-item-chevron]):first-child)_[data-slot=list-item]:not(:has(>:is(svg,img,[data-slot=list-item-lead]):not([data-slot=list-item-check],[data-slot=list-item-chevron]):first-child))]:pl-[30px]',
].join(' ')

/**
 * A row: the md control's height and type, its corner the popup's less the popup's padding so the
 * two stay concentric. The highlight fades in and out over 150ms, so running the pointer down a list
 * hands the ground from row to row. Icons are 14px at the system stroke whatever the call site
 * passes, so a menu never mixes weights. A row that leads with an
 * icon or a lead starts 5px in and gives it a 20px lane, 5px from the label: the icon centres in
 * the lane with 3px either side, so it paints 8px from its word and every label sits at 30px.
 */
export const listItemVariants = cva(
  [
    'group/item corner-squircle flex min-h-7 w-full shrink-0 cursor-default select-none items-center gap-2 rounded-[calc(var(--radius-lg)-4px)] px-[7px] py-1 text-left text-sm font-normal outline-none',
    '[&_svg]:size-3.5 [&_svg]:shrink-0 [&_svg.lucide]:[stroke-width:var(--icon-stroke-width)]',
    'has-[>:is(svg,img,[data-slot=list-item-lead]):not([data-slot=list-item-check],[data-slot=list-item-chevron]):first-child]:gap-[5px] has-[>:is(svg,img,[data-slot=list-item-lead]):not([data-slot=list-item-check],[data-slot=list-item-chevron]):first-child]:pl-[5px]',
    '[&>:is(svg,img):not([data-slot]):first-child]:mx-[3px]',
    // With a description the row grows to fit and its icon, check and chevron stay on the title's
    // line: 5px of padding centres an 18px line in 28, and 2px centres a 14px glyph on it.
    'has-[[data-slot=list-item-description]]:items-start has-[[data-slot=list-item-description]]:py-[5px]',
    '[&:has([data-slot=list-item-description])>:is(svg,img,[data-slot=list-item-check])]:mt-0.5',
    // An arbitrary property, because Tailwind drops an arbitrary ease class as ambiguous with the animation plugin.
    'transition-[background-color,color] duration-150 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
    'data-[disabled]:text-foreground-disabled data-[disabled]:cursor-not-allowed',
  ],
  {
    variants: {
      tone: {
        neutral:
          'text-foreground data-[highlighted]:bg-element-hover active:bg-element-active data-[popup-open]:bg-element-hover',
        danger:
          'text-danger-subtle data-[highlighted]:bg-danger data-[highlighted]:text-on-danger active:bg-danger-active active:text-on-danger',
      },
    },
    defaultVariants: {
      tone: 'neutral',
    },
  },
)

export type ListItemTone = NonNullable<VariantProps<typeof listItemVariants>['tone']>

/**
 * The 20px lane filled by a node that is not an icon: a workspace tile, a person's face. The node
 * fills it, and its own corner and ground are its business. Same lane as an icon's, so a list
 * mixing the two keeps one column.
 */
export const listItemLead = 'flex size-5 shrink-0 items-center justify-center [&_svg]:size-auto'

/** The label column of a row: fills, and ends in an ellipsis rather than pushing the trailing parts out. */
export const listItemLabel = 'min-w-0 flex-1 truncate'

/** A title with a description under it: the column fills, the title truncates, the description wraps. */
export const listItemStack = 'flex min-w-0 flex-1 flex-col gap-0.5'

/** The second line of a row: secondary ink, smaller, wrapping. */
export const listItemDescription =
  'text-foreground-secondary text-pretty text-xs group-data-[disabled]/item:text-foreground-disabled'

/** A shortcut, value or count at the row's end, set back in the secondary ink. */
export const listItemTrailing =
  'text-foreground-secondary ml-auto shrink-0 pl-2 group-data-[disabled]/item:text-foreground-disabled'

/**
 * The chosen mark, trailing so labels keep one left edge whichever row is picked, in the indicator
 * blue. Pulled 2px into the padding because the glyph's box carries air on its right.
 */
export const listItemCheck = 'text-indicator order-last -mr-0.5 ml-auto flex shrink-0 pl-2'

/** The submenu chevron, pulled out by its own empty side so its ink sits where a shortcut's would. */
export const listItemChevron = 'text-foreground-secondary order-last -mr-[5px] ml-auto shrink-0'

export const listGroupLabel =
  'text-foreground-secondary cursor-default select-none px-[7px] pb-1 pt-2 text-xs first:pt-1'

/** A hairline inset from the popup's edge, with the row gap's missing pixel taken back each side. */
export const listSeparator = 'mx-1.5 my-[3px] h-0 shrink-0 border-t border-border'
